import { computed, reactive, ref, watch } from "vue";
import { useSessionStorage }              from "vue-composable";
import SelectedPlan                       from "src/composables/SelectedPlan";
import ServiceLocation                    from "src/composables/ServiceLocation";
import ServiceStartDate                   from "src/composables/ServiceStartDate";
import CustomerInformation                from "src/composables/CustomerInformation";
import { orders_api }                     from "boot/axios";
import UsageFilters                       from "src/composables/UsageFilters";
import Plans                              from "src/composables/Plans";
import { set_fs_url_cookie }              from "boot/fullstory";
import html2canvas                        from "html2canvas";
import PlanFilters                        from "src/composables/PlanFilters";
import { Info }                           from "luxon";
import Tdsps                              from "src/composables/Tdsps";
import Supabase                           from "src/composables/Supabase";

const deposit_payment_error_codes = ['identity_payment_required', 'payment_amount_does_not_match', 'payment_url_does_not_match', 'no_payment_submitted', 'payment_declined'];

const step = ref('address');

const { storage: order_id } = useSessionStorage('order_id', null)
const order = ref(null);
const previous_order_alerted = ref(false);
const is_dirty = ref(false);

const order_status = computed(() => {
  if (order.value) {
    if (order.value.lock === 'complete') {
      const deposit_payment_required = order.value?.deposit_details?.amount_due > order.value?.deposit_details?.amount_paid;
      let has_post_order_actions = false;
      if (order.value?.post_order_actions) {
        for (const action of order.value?.post_order_actions) {
          if (['pev', 'resolve_fraud_alert', 'pay_bad_debt'].includes(action)) {
            has_post_order_actions = true;
            break;
          }
        }
      }
      if (deposit_payment_required || has_post_order_actions) {
        return 'confirmation_action_required';
      }

      return 'complete'
    }
    else if (order.value.errors?.length === 1) {

      const error = order.value.errors[0];


      switch (error.error) {
        case 'compare_power_error':
        case 'provider_error':
        case 'provider_unmapped_error':
        case 'unknown_error':
          return 'pending';
        case 'identity_fraud_alert_continue':
        case 'identity_frozen_continue_deposit':
        case 'identity_bad_debt_continue':
        case 'post_enrollment_verification_needed':
        case 'identity_deposit_required_continue':
          if (order.value.deposit_options) {
            return 'alternative_deposit_options';
          }
          return 'agreements';
        default:
          return error.error;
        //     return ''
      }

    }
    else if (order.value.errors?.length > 1) {
      const has_agreements = order.value.errors.some(x => [
          'identity_deposit_required_continue',
          'identity_fraud_alert_continue',
          'identity_frozen_continue_deposit',
          'identity_bad_debt_continue',
          'post_enrollment_verification_needed'
        ].includes(x.error)
      );
      if (has_agreements) {
        return 'agreements';
      }
      else {
        return order.value.errors[0];
      }
    }
    else {
      return 'unknown'
    }
  }
})

const mark_complete_page_viewed = async function ({ order_id, post_order_actions }) {
  try {
    const payload = {}
    if (post_order_actions) {
      payload.confirmation_page_type = 'post_order_actions'
    }
    const { data } = await orders_api.patch(`${ process.env.VUE_APP_ORDER_SERVICE_URL }/api/orders/texas_retail_electricity/${ order_id }/mark_complete_page_viewed`,
      payload,
      {
        responseType: 'json'
      });
    return data;
  }
  catch (err) {
    console.error(err);
  }
}

const submit_order = async function ({
                                       plan_id,
                                       service_location_key,
                                       selected_start_date,
                                       customer_information,
                                       billing_address,
                                       options,
                                       meta_data
                                     }) {
  try {
    set_fs_url_cookie()
    const { data } = await orders_api.post(`${ process.env.VUE_APP_ORDER_SERVICE_URL }/api/orders/texas_retail_electricity`,
      {
        plan_id,
        service_location_key,
        selected_start_date,
        customer_information,
        billing_address,
        options,
        meta_data
      },
      {
        responseType: 'json'
      });
    return data;
  }
  catch (err) {
    throw err;
  }
}

const get_order_status = async function ({ order_id }) {
  try {
    const {
      status,
      data
    } = await orders_api.get(`${ process.env.VUE_APP_ORDER_SERVICE_URL }/api/orders/texas_retail_electricity/${ order_id }/status`)
    if (status === 200) {
      order.value = data;
      return data;
    }

    if (data.customer_check_response?.value?.deposit_amount) {
      try {
        order.value.customer_check_response.value.deposit_amount = parseFloat(data.customer_check_response.value.deposit_amount);
      }
      catch (err) {
        console.error('deposit amount is not a number')
      }
    }

    if (data.order_submit_response?.value?.deposit_amount) {
      try {
        order.value.order_submit_response.value.deposit_amount = parseFloat(data.order_submit_response.value.deposit_amount);
      }
      catch (err) {
        console.error('deposit amount is not a number')
      }
    }

    return null;
  }
  catch (err) {
    return null
    // throw err;
  }
}
const { calculate_plan_for_usage, historical_usage_plan_map } = Plans();
const { filters } = PlanFilters()
const { selected_plan, plan_position, featured_plan_position } = SelectedPlan();
const { service_location, map_service_location, owns_property, property_type } = ServiceLocation();
const {
  usage_selected,
  historical_usage,
  usage_type,
  show_historical_usage,
  historical_usage_values,
  is_live_link_usage
} = UsageFilters();
const { is_authenticated, user } = Supabase()
const {
  first_name,
  last_name,
  email,
  phone_number,
  date_of_birth,
  // social_security_number,
  // drivers_license_state,
  // drivers_license_number,
  current_address,
  billing_address,
  spanish,
  clear_all_customer_information
} = CustomerInformation();


const { selected_start_date, action } = ServiceStartDate();

const order_properties = reactive({
  first_name,
  last_name,
  email,
  phone_number,
  date_of_birth,
  // social_security_number,
  // drivers_license_state,
  // drivers_license_number,
  current_address,
  billing_address,
  spanish,
  selected_start_date,
  action,
  service_location,
  owns_property,
  property_type
})
watch(order_properties, (to, from) => {
  is_dirty.value = true;
})

const order_submit = async function ({ social_security_number, drivers_license_number, drivers_license_state }) {
  try {
    const order_request = {
      plan_id: selected_plan.value._id,
      service_location_key: service_location.value.esiid,
      ...selected_start_date.value && { selected_start_date: selected_start_date.value },
      customer_information: {
        first_name: first_name.value,
        last_name: last_name.value,
        email: email.value,
        phone_number: phone_number.value,
        date_of_birth: date_of_birth.value,
        ...social_security_number && { social_security_number: social_security_number },
        ...drivers_license_number && { drivers_license_number: drivers_license_number },
        ...drivers_license_state && { drivers_license_state: drivers_license_state },
        ...current_address.value?.line_1
        && current_address.value?.city
        && current_address.value?.state
        && current_address.value?.zip_code
        && {
          current_address: {
            line_1: current_address.value.line_1,
            ...current_address.value.line_2 && { line_2: current_address.value.line_2 },
            city: current_address.value.city,
            state: current_address.value.state,
            zip_code: current_address.value.zip_code,
          }
        }
      },
      billing_address: {
        line_1: billing_address.value.line_1,
        ...billing_address.value.line_2 && { line_2: billing_address.value.line_2 },
        city: billing_address.value.city,
        state: billing_address.value.state,
        zip_code: billing_address.value.zip_code,
      },
      options: {
        spanish: spanish.value,
        move_in: action.value === 'Move'
      },
      meta_data: {
        property_type: property_type.value,
        owns_property: owns_property.value,
        channel: 'comparepower.com web portal',
        plan_position: plan_position.value,
        featured_plan_position: featured_plan_position.value,
        filters
      }
    };

    if (show_historical_usage.value) {
      order_request.meta_data.selected_profile = historical_usage_values.value.map(x => parseInt(x));
    }
    else {
      order_request.meta_data.selected_usage = parseInt(usage_selected.value);
    }

    if (is_authenticated.value) {
      order_request.meta_data.logged_in = true
      order_request.meta_data.logged_in_user = user.value
    }

    if (is_live_link_usage.value) {
      order_request.meta_data.live_link = true
    }

    if (order_id.value) {
      if (!order_request.selected_start_date) {
        order_request.remove_start_date = true;
      }
      await update_order(order_request)
      take_screenshot({ order_id: order_id.value });
    }
    else {
      const { _id } = await submit_order(order_request)
      order_id.value = _id;
      take_screenshot({ order_id: order_id.value });
    }
    return order_id.value;
  }
  catch (err) {
    throw err
  }
}

const update_order = async function (update) {
  if (order_id.value) {
    const { data } = await orders_api.patch(`${ process.env.VUE_APP_ORDER_SERVICE_URL }/api/orders/texas_retail_electricity/${ order_id.value }`,
      update,
      {
        responseType: 'json'
      });
    return data;
  }
}

const update_customer_info = async function (update) {
  if (order_id.value) {
    const { data } = await orders_api.patch(`${ process.env.VUE_APP_ORDER_SERVICE_URL }/api/orders/texas_retail_electricity/${ order_id.value }/update_customer_info`,
      update,
      {
        responseType: 'json'
      });
    return data;
  }
}

const get_deposit_payment_link = async function () {
  if (order_id.value) {
    const { data } = await orders_api.get(`${ process.env.VUE_APP_ORDER_SERVICE_URL }/api/orders/texas_retail_electricity/${ order_id.value }/get_deposit_payment_link`,
      {
        responseType: 'json'
      });
    return data.value.url;
  }
}

const make_payment = async function (payload) {
  if (order_id.value) {
    const { data } = await orders_api.patch(`${ process.env.VUE_APP_ORDER_SERVICE_URL }/api/orders/texas_retail_electricity/${ order_id.value }/make_payment_request`,
      payload,
      {
        responseType: 'json'
      });
    return data;
  }
}

const mark_order_as_paid = async function ({ order_id, payload }) {
  if (order_id) {
    const { data } = await orders_api.patch(`${ process.env.VUE_APP_ORDER_SERVICE_URL }/api/orders/texas_retail_electricity/${ order_id }/mark_deposit_paid`,
      payload,
      {
        responseType: 'json'
      });
    return data;

  }
}

const get_alternative_deposit_options = async function () {
  if (order_id.value) {
    const { data } = await orders_api.get(`${ process.env.VUE_APP_ORDER_SERVICE_URL }/api/orders/texas_retail_electricity/${ order_id.value }/alternative_deposit_options`,
      {
        responseType: 'json'
      });
    return data;
  }
}

const submit_verification_code = async function ({ code }) {
  if (order_id.value) {
    const { data } = await orders_api.get(`${ process.env.VUE_APP_ORDER_SERVICE_URL }/api/orders/texas_retail_electricity/${ order_id.value }/sms/${ code }`,
      {
        responseType: 'json'
      });
    return data;
  }
}

const request_verification_code = async function () {
  if (order_id.value) {
    const { data } = await orders_api.get(`${ process.env.VUE_APP_ORDER_SERVICE_URL }/api/orders/texas_retail_electricity/${ order_id.value }/sms`,
      {
        responseType: 'json'
      });
    return data;
  }
}

const select_alternative_deposit_option = async function ({ plan_id }) {
  if (order_id.value) {
    const { data } = await orders_api.patch(`${ process.env.VUE_APP_ORDER_SERVICE_URL }/api/orders/texas_retail_electricity/${ order_id.value }/alternative_deposit_options`,
      { plan_id },
      {
        responseType: 'json'
      });

    take_screenshot({ order_id: order_id.value });
    return data;
  }
}

const order_processing = ref(false);

const process_order = async function () {
  const { zip_code, selected_tdsp } = Tdsps();
  try {
    order_processing.value = true;
    order.value = await get_order_status({ order_id: order_id.value });
    if (order.value) {
      if (order.value.request?.meta_data?.selected_usage) {
        usage_selected.value = order.value.request?.meta_data?.selected_usage;
        usage_type.value = 'kWh';
      }
      else if (order.value.request?.meta_data?.selected_profile) {

        historical_usage.value = Info.months("long").map((x, index) => {
          return {
            month: x,
            usage: order.value.request?.meta_data?.selected_profile[index] || null,
            short_month: x.substring(0, 3)
          };
        })
        usage_type.value = 'historical';
      }

      if (show_historical_usage.value) {
        for (const hu of historical_usage_values.value) {
          order.value.selected_plan.value[`display_pricing_${ hu }`] = await calculate_plan_for_usage({
            plan_id: order.value.selected_plan.value._id,
            usage: hu
          })
        }
        order.value.selected_plan.value = historical_usage_plan_map(order.value.selected_plan.value)
      }
      else if (![500, 1000, 2000].includes(usage_selected.value) && order.value.selected_plan?.value) {
        order.value.selected_plan.value[`display_pricing_${ usage_selected.value }`] = await calculate_plan_for_usage({
          plan_id: order.value.selected_plan.value._id,
          usage: usage_selected.value
        })
      }
      if (['deposit_required', 'complete', 'pending'].includes(order_status.value)) {
        service_location.value = null;
        selected_plan.value = null;
        selected_start_date.value = null;
        action.value = null;
        clear_all_customer_information()
      }
      else {
        selected_plan.value = order.value.selected_plan.value;
        selected_tdsp.value = order.value.selected_plan.value.tdsp;
        if (order.value?.service_location?.value) {
          order.value.service_location.value = map_service_location(order.value.service_location.value)
          service_location.value = order.value.service_location.value;
          zip_code.value = order.value.service_location.value.zip_code;
        }
        selected_start_date.value = order.value?.request?.selected_start_date || null;
        action.value = order.value?.request?.options?.move_in ? 'Move' : !!selected_start_date.value ? 'Switch' : 'ASAP'
        first_name.value = order.value.request.customer_information.first_name
        last_name.value = order.value.request.customer_information.last_name
        email.value = order.value.request.customer_information.email
        phone_number.value = order.value.request.customer_information.phone_number
        property_type.value = order.value.request?.meta_data?.property_type;
        owns_property.value = order.value.request?.meta_data?.owns_property;
      }
    }
  }
  catch (err) {
    throw err;
  }
  finally {
    order_processing.value = false;
  }
}

const take_screenshot = function ({ order_id }) {
  let scale = 1;
  const width = screen.width;
  const height = screen.height;
  const pixels = width * height;
  if (pixels > 5000000) {
    scale = 5000000 / pixels
  }
  html2canvas(document.body, {
    scrollX: 0,
    scrollY: 0,
    scale,
    useCORS: true,
    allowTaint: true
  })
    .then(function (canvas) {
      canvas.toBlob(async (blob) => {
        orders_api.post(`/api/orders/texas_retail_electricity/${ order_id }/screenshots/authorizations`,
          blob,
          {
            headers: {
              'content-type': 'multipart/form-data'
            }
          }
        )
      });
    })
    .catch(function (err) {
      console.error(err);
    })
}

export default () => {
  return {
    is_dirty,
    deposit_payment_error_codes,
    step,
    order_id,
    order,
    submit_order,
    submit_verification_code,
    request_verification_code,
    get_order_status,
    order_status,
    order_submit,
    update_order,
    update_customer_info,
    get_deposit_payment_link,
    make_payment,
    mark_order_as_paid,
    mark_complete_page_viewed,
    get_alternative_deposit_options,
    select_alternative_deposit_option,

    order_processing,
    process_order,
    previous_order_alerted
  }
}
